// src/pages/patient/EmergencyRequest.js
import React, { useState, useEffect } from 'react';
import { 
  Ambulance, MapPin, Phone, Clock,  
  Navigation,  Heart, Activity
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from '../../context/LocationContext';
import patientService from '../../services/patientService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Alert from '../../components/ui/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/ui/Badge';

const EmergencyRequest = () => {
  const { user } = useAuth();
  const { userLocation, requestLocation } = useLocation();

  const [loading, setLoading] = useState(false);
  const [availableAmbulances, setAvailableAmbulances] = useState([]);
  const [activeRequest, setActiveRequest] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [requestData, setRequestData] = useState({
    emergency: true,
    description: '',
    contactPhone: user?.phone || '',
    location: {
      coordinates: [],
      address: ''
    },
    destination: {
      coordinates: [],
      address: ''
    },
    urgency: 'critical'
  });

  useEffect(() => {
    checkActiveRequest();
    if (userLocation) {
      setRequestData(prev => ({
        ...prev,
        location: {
          coordinates: [userLocation.longitude, userLocation.latitude],
          address: userLocation.address || ''
        }
      }));
    }
  }, [userLocation]);

  useEffect(() => {
    if (requestData.location.coordinates.length > 0) {
      loadAvailableAmbulances();
    }
  }, [requestData.location.coordinates]);

  const checkActiveRequest = async () => {
    try {
      const response = await patientService.getAmbulanceRequests({
        patientId: user.id,
        status: ['pending', 'assigned', 'accepted', 'enroute', 'arrived']
      });

      if (response.data?.data?.requests?.length > 0) {
        setActiveRequest(response.data.data.requests[0]);
      }
    } catch (error) {
      console.error('Check active request error:', error);
    }
  };

  const loadAvailableAmbulances = async () => {
    try {
      setLoading(true);
      setError('');

      const [longitude, latitude] = requestData.location.coordinates;
      const response = await patientService.findNearbyAmbulances({
        latitude,
        longitude,
        urgency: requestData.urgency,
        limit: 10
      });

      setAvailableAmbulances(response.data?.data?.ambulances || []);
    } catch (error) {
      console.error('Load ambulances error:', error);
      setError('Failed to load available ambulances. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationRequest = async () => {
    try {
      setLoading(true);
      setError('');
      
      const location = await requestLocation();
      
      setRequestData(prev => ({
        ...prev,
        location: {
          coordinates: [location.longitude, location.latitude],
          address: location.address || `${location.latitude}, ${location.longitude}`
        }
      }));
      
      setSuccess('Location updated successfully');
    } catch (error) {
      setError('Failed to get your location. Please enable location services.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();

    // Validation
    if (!requestData.description.trim()) {
      setError('Please describe your emergency');
      return;
    }

    if (!requestData.contactPhone) {
      setError('Contact phone number is required');
      return;
    }

    if (requestData.location.coordinates.length === 0) {
      setError('Your location is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await patientService.requestAmbulance({
        ...requestData,
        patientId: user.id
      });

      setSuccess('Emergency request submitted successfully! An ambulance has been dispatched.');
      setActiveRequest(response.data?.data);
      
      // Reset form
      setRequestData(prev => ({
        ...prev,
        description: '',
        destination: { coordinates: [], address: '' }
      }));

    } catch (error) {
      console.error('Submit request error:', error);
      setError(error.response?.data?.message || 'Failed to submit emergency request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!activeRequest) return;

    if (!window.confirm('Are you sure you want to cancel this emergency request?')) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await patientService.cancelAmbulanceRequest(activeRequest._id, 'Patient cancelled');
      
      setSuccess('Request cancelled successfully');
      setActiveRequest(null);
      
    } catch (error) {
      console.error('Cancel request error:', error);
      setError('Failed to cancel request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      enroute: 'bg-purple-100 text-purple-800',
      arrived: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.pending;
  };

  if (activeRequest) {
    return (
      <div className="space-y-6">
        {/* Active Request Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">Active Emergency Request</h1>
              <p className="text-red-100">
                Help is on the way! Track your ambulance below.
              </p>
            </div>
            <Activity className="w-12 h-12 animate-pulse" />
          </div>
        </div>

        {/* Success Alert */}
        {success && (
          <Alert type="success" title="Success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert type="error" title="Error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Request Details */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Request Details</h2>
            <Badge className={getStatusColor(activeRequest.status)}>
              {activeRequest.status.toUpperCase()}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Patient Information */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Request ID</label>
                <p className="mt-1 text-gray-900">{activeRequest._id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Emergency Description</label>
                <p className="mt-1 text-gray-900">{activeRequest.description}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Contact Phone</label>
                <p className="mt-1 text-gray-900">{activeRequest.contactPhone}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Pickup Location</label>
                <p className="mt-1 text-gray-900">{activeRequest.location?.address || 'Location set'}</p>
              </div>
            </div>

            {/* Ambulance Information */}
            {activeRequest.driver && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Driver Name</label>
                  <p className="mt-1 text-gray-900">
                    {activeRequest.driver.firstName} {activeRequest.driver.lastName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Driver Phone</label>
                  <p className="mt-1 text-gray-900">{activeRequest.driver.phone}</p>
                </div>
                {activeRequest.estimatedArrival && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Estimated Arrival</label>
                    <p className="mt-1 text-gray-900">
                      {new Date(activeRequest.estimatedArrival).toLocaleTimeString()}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-4 mt-6 pt-6 border-t">
            {activeRequest.driver && (
              <Button
                onClick={() => window.location.href = `tel:${activeRequest.driver.phone}`}
                variant="outline"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Driver
              </Button>
            )}
            
            {['pending', 'assigned'].includes(activeRequest.status) && (
              <Button
                onClick={handleCancelRequest}
                variant="danger"
                loading={loading}
              >
                Cancel Request
              </Button>
            )}
          </div>
        </div>

        {/* Map Placeholder */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Tracking</h3>
          <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center">
            <div className="text-center">
              <Navigation className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">Map integration coming soon</p>
              <p className="text-sm text-gray-500 mt-1">Track your ambulance in real-time</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Emergency Ambulance Services</h1>
            <p className="text-red-100">
              Request immediate ambulance assistance - Available 24/7
            </p>
          </div>
          <Heart className="w-12 h-12" />
        </div>
      </div>

      {/* Success Alert */}
      {success && (
        <Alert type="success" title="Success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert type="error" title="Error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Emergency Notice */}
      <Alert type="warning" title="Important Notice">
        <p className="mb-2">
          This is for medical emergencies only. If you need immediate life-saving assistance,
          please also call emergency services at <strong>999</strong> or <strong>911</strong>.
        </p>
        <p>
          Our ambulance service complements local emergency services and provides additional
          transport options when needed.
        </p>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Request Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmitRequest} className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Request Emergency Ambulance</h2>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Location *
              </label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Input
                    icon={MapPin}
                    value={requestData.location.address}
                    onChange={(e) => setRequestData(prev => ({
                      ...prev,
                      location: { ...prev.location, address: e.target.value }
                    }))}
                    placeholder="Enter your address"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleLocationRequest}
                    loading={loading}
                  >
                    <Navigation className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Click the location icon to use your current location
                </p>
              </div>
            </div>

            {/* Emergency Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe the Emergency *
              </label>
              <textarea
                value={requestData.description}
                onChange={(e) => setRequestData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Please describe the nature of the emergency (e.g., chest pain, difficulty breathing, severe bleeding, accident)"
                required
              />
            </div>

            {/* Contact Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Phone *
              </label>
              <Input
                icon={Phone}
                type="tel"
                value={requestData.contactPhone}
                onChange={(e) => setRequestData(prev => ({ ...prev, contactPhone: e.target.value }))}
                placeholder="Enter contact phone number"
                required
              />
            </div>

            {/* Urgency Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Urgency Level *
              </label>
              <Select
                value={requestData.urgency}
                onChange={(e) => setRequestData(prev => ({ ...prev, urgency: e.target.value }))}
              >
                <option value="critical">Critical - Life Threatening</option>
                <option value="high">High - Urgent Medical Attention Needed</option>
                <option value="medium">Medium - Medical Attention Required</option>
              </Select>
            </div>

            {/* Destination (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Hospital (Optional)
              </label>
              <Input
                icon={MapPin}
                value={requestData.destination.address}
                onChange={(e) => setRequestData(prev => ({
                  ...prev,
                  destination: { ...prev.destination, address: e.target.value }
                }))}
                placeholder="Leave blank for nearest hospital"
              />
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t">
              <Button type="submit" loading={loading} className="bg-red-600 hover:bg-red-700">
                <Ambulance className="w-4 h-4 mr-2" />
                Request Emergency Ambulance
              </Button>
            </div>
          </form>
        </div>

        {/* Available Ambulances */}
        <div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Available Ambulances ({availableAmbulances.length})
            </h3>

            {loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : availableAmbulances.length > 0 ? (
              <div className="space-y-3">
                {availableAmbulances.slice(0, 5).map((ambulance) => (
                  <div key={ambulance._id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        {ambulance.vehicleNumber}
                      </span>
                      <Badge className="bg-green-100 text-green-800">
                        Available
                      </Badge>
                    </div>
                    <div className="flex items-center text-xs text-gray-600 space-x-3">
                      <div className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {ambulance.distance?.toFixed(1)} km away
                      </div>
                      {ambulance.estimatedArrival && (
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          ~{ambulance.estimatedArrival} min
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Ambulance className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  {requestData.location.coordinates.length === 0
                    ? 'Set your location to see available ambulances'
                    : 'Searching for available ambulances...'}
                </p>
              </div>
            )}
          </div>

          {/* Emergency Contacts */}
          <div className="bg-red-50 rounded-lg border border-red-200 p-6 mt-6">
            <h3 className="text-lg font-semibold text-red-900 mb-4">
              Emergency Contacts
            </h3>
            <div className="space-y-3">
              <a
                href="tel:999"
                className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-red-50 transition-colors"
              >
                <span className="font-medium text-gray-900">Police Emergency</span>
                <span className="text-red-600 font-bold">999</span>
              </a>
              <a
                href="tel:911"
                className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-red-50 transition-colors"
              >
                <span className="font-medium text-gray-900">Medical Emergency</span>
                <span className="text-red-600 font-bold">911</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyRequest;